/**
 * Minimal fetch wrapper for the NorthStar JSON API.
 *
 * - Always sends cookies ("include") so the HttpOnly session cookie flows
 *   on same-origin requests (dev via Vite proxy, prod via CloudFront).
 * - On 401, navigates the browser to /api/auth/start with a return_to so the
 *   server can decide whether to render the SPA dev login page or bounce
 *   straight to Auth0. The user lands back at whatever page they tried to
 *   visit. When the path is the site root (`/`), we omit return_to entirely
 *   so the server falls back to its default home (`/dashboard`).
 * - Throws on any non-2xx with the parsed body attached.
 */

/**
 * Standard error shape the server emits on 4xx/5xx. Matches the
 * ApiErrorBody struct in src/transports/api/errors.rs.
 *
 *   { error: "conflict",
 *     message?: "A segment with that name already exists.",
 *     field_errors?: { name: "Already exists" } }
 */
export interface ApiErrorBody {
  error: string;
  message?: string;
  field_errors?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`API ${status}`);
  }

  /**
   * Returns the structured error body when the server sent our standard
   * ApiErrorBody shape; null otherwise (e.g. network error, unknown
   * response format). Use this to surface `field_errors` or `message` in
   * form error handlers.
   */
  get asErrorBody(): ApiErrorBody | null {
    if (this.body && typeof this.body === "object" && "error" in this.body) {
      return this.body as ApiErrorBody;
    }
    return null;
  }
}

/**
 * Builds the URL to navigate to on a 401. Captures the current pathname +
 * search as a `return_to` so the user lands back where they were after
 * signing in, *unless* they were on the site root (`/`) — in which case we
 * omit return_to and let the server default to its post-login home
 * (`/dashboard`). Hitting `/` post-login is a dead landing because the SPA
 * has no route there.
 */
export function authStartUrl(): string {
  const pathWithSearch = window.location.pathname + window.location.search;
  if (pathWithSearch === "/") return "/api/auth/start";
  return `/api/auth/start?return_to=${encodeURIComponent(pathWithSearch)}`;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "include",
    headers: body
      ? {
          "content-type": "application/json",
          accept: "application/json",
        }
      : { accept: "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 401) {
    window.location.assign(authStartUrl());
    // Return a never-resolving promise so callers don't try to continue.
    return new Promise<T>(() => {});
  }

  const text = await res.text();
  const parsed = text ? safeParse(text) : null;
  if (!res.ok) throw new ApiError(res.status, parsed ?? text);
  return parsed as T;
}

/**
 * Multipart/form-data POST. Don't set the Content-Type header manually —
 * the browser sets it (including the multipart boundary) automatically
 * when `body` is a `FormData` instance.
 *
 * Same 401-redirect + ApiError semantics as `request`. Response body can
 * be JSON (DTO) or empty (204); both are handled.
 */
async function upload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { accept: "application/json" },
    body: form,
  });

  if (res.status === 401) {
    window.location.assign(authStartUrl());
    return new Promise<T>(() => {});
  }

  const text = await res.text();
  const parsed = text ? safeParse(text) : null;
  if (!res.ok) throw new ApiError(res.status, parsed ?? text);
  return parsed as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * POST with a JSON body that returns a binary file (XLSX, PDF, etc.).
 * Triggers a browser save dialog using the `Content-Disposition`
 * filename when the server sets one. Same 401-redirect + ApiError
 * semantics as the JSON helpers — on a non-2xx the server's JSON
 * error body is parsed and thrown so the caller's `mapServerErrors`
 * works unchanged.
 */
async function download(path: string, body: unknown): Promise<void> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      accept: "application/octet-stream, application/json",
    },
    body: JSON.stringify(body),
  });

  if (res.status === 401) {
    window.location.assign(authStartUrl());
    return new Promise<void>(() => {});
  }

  if (!res.ok) {
    // Server emits the standard ApiErrorBody JSON on 4xx/5xx — parse
    // and throw so callers can branch on the same `ApiError` they
    // handle for JSON endpoints.
    const text = await res.text();
    throw new ApiError(res.status, safeParse(text) ?? text);
  }

  const filename = parseContentDispositionFilename(
    res.headers.get("content-disposition") ?? "",
  );
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename ?? "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * GET a binary file (CSV export, etc.) and trigger a browser save dialog.
 * The mirror of `download` for endpoints that take no body — use this instead
 * of a naked `<a href>` so a 403/500 surfaces as an `ApiError` (→ a toast) and a
 * 401 redirects to login, rather than the browser navigating to the raw JSON
 * error page.
 */
async function downloadGet(path: string): Promise<void> {
  const res = await fetch(path, {
    method: "GET",
    credentials: "include",
    headers: { accept: "application/octet-stream, text/csv, application/json" },
  });

  if (res.status === 401) {
    window.location.assign(authStartUrl());
    return new Promise<void>(() => {});
  }

  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, safeParse(text) ?? text);
  }

  const filename = parseContentDispositionFilename(
    res.headers.get("content-disposition") ?? "",
  );
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename ?? "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * GET a binary response and hand back the raw `Blob` — for content the
 * caller renders itself (a PDF fed to `pdfjs-dist`) rather than saves to
 * disk. The mirror of `downloadGet`, minus the save-dialog trigger. Same
 * 401-redirect + `ApiError` semantics as every other helper here.
 */
async function getBlob(path: string): Promise<Blob> {
  const res = await fetch(path, {
    method: "GET",
    credentials: "include",
    headers: { accept: "*/*" },
  });

  if (res.status === 401) {
    window.location.assign(authStartUrl());
    return new Promise<Blob>(() => {});
  }

  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(res.status, safeParse(text) ?? text);
  }

  return res.blob();
}

/** Pulls `filename="..."` out of a Content-Disposition header. */
function parseContentDispositionFilename(value: string): string | null {
  // RFC 5987 `filename*` wins when present: it carries the real name for
  // servers that must also emit an ASCII-folded `filename` fallback (a header
  // value cannot hold the accents in a client name). Falls back to the plain
  // form for every endpoint that sends only that.
  const extended = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(value);
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // A malformed percent-escape must not lose the download; fall through
      // to the ASCII form rather than throwing.
    }
  }
  const match = /filename\s*=\s*"?([^"]+)"?/i.exec(value);
  return match ? match[1] : null;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
  upload: <T>(path: string, form: FormData) => upload<T>(path, form),
  /** POST a JSON body, save the binary response as a file. */
  download: (path: string, body: unknown) => download(path, body),
  /** GET a binary file (no body), save the response as a file. */
  downloadGet: (path: string) => downloadGet(path),
  /** GET a binary response as a `Blob`, for content the caller renders
   *  itself instead of saving to disk. */
  getBlob: (path: string) => getBlob(path),
};
